import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { Bell } from 'lucide-react';
import type { Notification } from '@xdocs/schemas';
import { Button, EmptyState, Modal, SegmentedControl, Skeleton, cn } from '@xdocs/ui';
import { NotificationItem, routeForRef } from './NotificationItem.js';
import { useExpandedGroups } from './useExpandedGroups.js';
import { useNotifications } from './useNotifications.js';
import { useNotificationsT } from './useNotificationsT.js';
import styles from './NotificationsBell.module.css';

/**
 * Bell + unread dot (F33). Desktop and mobile both render through `<Modal>`
 * for this slice (deliberate simplification — see this task's returned
 * open-issues list: `@xdocs/ui` has no dedicated anchored-popover/bottom-
 * sheet primitive yet, and `Modal` already gives correct focus-trap/Esc/
 * restore-focus semantics; CLAUDE_DESIGN.md §6 calls for a lighter anchored
 * popover on desktop specifically).
 */
export function NotificationsBell(): ReactElement {
  const { t } = useNotificationsT();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const {
    items,
    isLoading,
    isError,
    refetch,
    unreadOnly,
    setUnreadOnly,
    hasMore,
    loadMore,
    isLoadingMore,
    loadMoreError,
    unreadCount,
    markRead,
  } = useNotifications();
  const { isExpanded, toggle: toggleExpanded } = useExpandedGroups();

  function handleOpenItem(item: Notification): void {
    if (item.readAt === null) void markRead(item.id);
    const route = routeForRef(item.refType, item.refId);
    if (route) {
      setOpen(false);
      void navigate(route);
    }
  }

  const showUnreadBadge = unreadCount !== null && unreadCount > 0;
  const isEmpty = !isLoading && !isError && items.length === 0;

  return (
    <>
      <span className={styles['bellWrapper']}>
        <Button size="icon" variant="ghost" aria-label={t('notifications:bell.label')} onClick={() => setOpen(true)}>
          <Bell size={18} aria-hidden="true" />
        </Button>
        {showUnreadBadge && (
          <span className={cn(styles['dot'])} aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {showUnreadBadge && <span className={styles['visuallyHidden']}>{t('notifications:bell.unread', { count: unreadCount })}</span>}
      </span>

      <Modal open={open} onClose={() => setOpen(false)} title={t('notifications:panel.title')} size="md" scrollableBody>
        <div className={styles['panel']}>
          <div className={styles['panelHeader']}>
            <SegmentedControl
              label={t('notifications:panel.title')}
              value={unreadOnly ? 'unread' : 'all'}
              onChange={(value) => setUnreadOnly(value === 'unread')}
              options={[
                { value: 'all', label: t('notifications:panel.filter.all') },
                { value: 'unread', label: t('notifications:panel.filter.unread') },
              ]}
            />
            <Button variant="ghost" size="sm" onClick={() => void markRead(null)}>
              {t('notifications:panel.markAllRead')}
            </Button>
          </div>

          {isError && (
            <EmptyState kind="error" title={t('notifications:error.title')} action={{ label: t('notifications:error.retry'), onClick: refetch }} />
          )}

          {!isError && isLoading && (
            <div aria-busy="true">
              <Skeleton shape="row" count={4} />
            </div>
          )}

          {!isError && isEmpty && (
            <EmptyState
              kind="first-use"
              title={unreadOnly ? t('notifications:empty.unread.title') : t('notifications:empty.title')}
              description={unreadOnly ? undefined : t('notifications:empty.description')}
            />
          )}

          {!isError && !isEmpty && (
            <ul className={styles['list']}>
              {items.map((item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  onOpen={handleOpenItem}
                  expanded={isExpanded(item.id)}
                  onToggleExpand={toggleExpanded}
                />
              ))}
            </ul>
          )}

          {!isError && hasMore && (
            <Button variant="ghost" size="sm" state={isLoadingMore ? 'loading' : 'idle'} onClick={loadMore}>
              {t('notifications:loadMore')}
            </Button>
          )}
          {loadMoreError && <p className={styles['loadMoreError']}>{t('notifications:loadMoreError')}</p>}
        </div>
      </Modal>
    </>
  );
}
